import { Fragment, useEffect, useState } from "react";
import api from "@/lib/api";
import { fmt, MAT_TYPE, EXTRA_TYPE_TABS } from "@/lib/ui";

const TYPES = [
  { key: "", label: "Tümü" }, { key: "tel", label: "Tel" }, { key: "tela", label: "Tela" },
  { key: "kraft", label: "Kraft" }, { key: "lamet", label: "Lamet" }, { key: "cring", label: "C-Ring" },
  ...EXTRA_TYPE_TABS,
];

const WEEK_COUNT = 5;

const hf = (label, fallback) => { const m = String(label || "").match(/H(\d+)/); return m ? `${parseInt(m[1])}. Hf` : (label || fallback || "—"); };

export default function DetailedStock() {
  const [data, setData] = useState({ horizon: [], materials: [] });
  const [pos, setPos] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [tab, setTab] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/projection?weeks=12"),
      api.get("/purchase-orders"),
      api.get("/materials"),
    ]).then(([p, po, m]) => {
      setData(p.data); setPos(po.data); setMaterials(m.data); setLoading(false);
    }).catch(() => { setError(true); setLoading(false); });
  }, []);

  const rows = data.materials.filter((m) => !tab || m.type === tab);
  const w = data.horizon.slice(0, WEEK_COUNT).map((h) => h.label); // ilk 5 hafta (örn. 40-44)
  const flowCols = 4 + Math.max(w.length * 2 - 1, 0) + 1;

  // Tablo 2: hafta x malzeme satın alma matrisi (5 haftalık pencere)
  const matName = (id) => materials.find((mm) => mm.id === id)?.name || "—";
  const poWeeks = w;
  const poMatIds = [...new Set(pos.filter((p) => p.status !== "kapali").map((p) => p.material_id))];
  const poCell = (mid, week) => pos.filter((p) => p.material_id === mid && p.expected_week === week && p.status !== "kapali")
    .reduce((a, p) => a + (p.quantity_ordered || 0), 0);

  return (
    <div className="space-y-8 animate-in-up" data-testid="detailed-stock-page">
      <div>
        <h1 className="font-head text-3xl font-bold tracking-tight">Detaylı Malzeme Stok Analiz</h1>
        <p className="text-[#94A3B8] text-sm mt-1">5 haftalık başlangıç stoğu, toplam ihtiyaç ve haftalık satın alma dökümü</p>
      </div>

      <div className="flex gap-1.5 flex-wrap" data-testid="ds-type-tabs">
        {TYPES.map((t) => (
          <button key={t.key} data-testid={`ds-type-${t.key || "all"}`} onClick={() => setTab(t.key)}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${tab === t.key ? "bg-[#2563EB] text-white" : "bg-[#131A26] border border-[#212D40] text-[#94A3B8] hover:text-white"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {error && <div className="bg-[#131A26] border border-[#EF4444]/40 text-[#EF4444] rounded-xl px-5 py-4 text-sm" data-testid="ds-error">Veriler yüklenemedi. Lütfen sayfayı yenileyin.</div>}

      {/* Tablo 1 */}
      <div className="bg-[#131A26] border border-[#212D40] rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-[#212D40] bg-[#0F1520] font-head font-semibold">1) Stok Akış Analizi</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="ds-flow-table">
            <thead>
              <tr className="text-left text-[#94A3B8] text-xs uppercase tracking-wider border-b border-[#212D40] bg-[#0F1520]">
                <th className="py-3 px-4">Malzeme</th>
                <th className="py-3 px-4 text-right">Mevcut Stok</th>
                <th className="py-3 px-4 text-right">Total İhtiyaç</th>
                <th className="py-3 px-4 text-right">{WEEK_COUNT} Hf İhtiyaç</th>
                {w.map((label, i) => (
                  <Fragment key={label || i}>
                    {i > 0 && <th className="py-3 px-4 text-right">Stok {hf(label, `${i + 1}. Hf`)}</th>}
                    <th className="py-3 px-4 text-right">{hf(label, `${i + 1}. Hf`)}</th>
                  </Fragment>
                ))}
                <th className="py-3 px-4">Tükenme Hf</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={flowCols} className="py-10 text-center text-[#64748B]">Yükleniyor...</td></tr>}
              {!loading && rows.map((m) => {
                const total = (m.weekly || []).reduce((a, x) => a + (x.consumption || 0), 0);
                const five = (m.weekly || []).slice(0, WEEK_COUNT).reduce((a, x) => a + (x.consumption || 0), 0);
                const wk = m.weekly || [];
                return (
                  <tr key={m.key} className="border-b border-[#1a2334] hover:bg-[#0F1520]" data-testid={`ds-row-${m.key}`}>
                    <td className="py-2.5 px-4 font-medium">
                      <span style={{ color: (MAT_TYPE[m.type] || {}).color }}>●</span> {m.name}
                    </td>
                    <td className="py-2.5 px-4 text-right font-num">{fmt(m.current_stock)} {m.unit}</td>
                    <td className="py-2.5 px-4 text-right font-num text-[#F59E0B]">{fmt(total)}</td>
                    <td className="py-2.5 px-4 text-right font-num">{fmt(five)}</td>
                    {w.map((label, i) => (
                      <Fragment key={label || i}>
                        {i > 0 && <td className="py-2.5 px-4 text-right font-num text-[#38BDF8]">{fmt(wk[i]?.opening || 0)}</td>}
                        <td className="py-2.5 px-4 text-right font-num">{fmt(wk[i]?.consumption || 0)}</td>
                      </Fragment>
                    ))}
                    <td className="py-2.5 px-4 font-num text-[#EF4444]">{m.runout_week || "—"}</td>
                  </tr>
                );
              })}
              {!loading && rows.length === 0 && <tr><td colSpan={flowCols} className="py-10 text-center text-[#64748B]">Kayıt yok.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Tablo 2 */}
      <div className="bg-[#131A26] border border-[#212D40] rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-[#212D40] bg-[#0F1520] font-head font-semibold">2) Haftalık Satın Alma Dökümü</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="ds-po-table">
            <thead>
              <tr className="text-left text-[#94A3B8] text-xs uppercase tracking-wider border-b border-[#212D40] bg-[#0F1520]">
                <th className="py-3 px-4">Malzeme</th>
                {poWeeks.map((wk, i) => <th key={wk || i} className="py-3 px-4 text-right whitespace-nowrap">{hf(wk, `${i + 1}. Hf`)}</th>)}
                <th className="py-3 px-4 text-right">Toplam</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={poWeeks.length + 2} className="py-10 text-center text-[#64748B]">Yükleniyor...</td></tr>}
              {!loading && poMatIds.map((mid) => {
                const rowTotal = poWeeks.reduce((a, wk) => a + poCell(mid, wk), 0);
                if (rowTotal === 0) return null;
                const unit = materials.find((mm) => mm.id === mid)?.unit || "";
                return (
                  <tr key={mid} className="border-b border-[#1a2334] hover:bg-[#0F1520]" data-testid={`ds-po-row-${mid}`}>
                    <td className="py-2.5 px-4 font-medium">{matName(mid)}</td>
                    {poWeeks.map((wk) => { const v = poCell(mid, wk); return <td key={wk} className="py-2.5 px-4 text-right font-num">{v ? fmt(v) : <span className="text-[#334155]">—</span>}</td>; })}
                    <td className="py-2.5 px-4 text-right font-num font-semibold text-[#10B981]">{fmt(rowTotal)} {unit}</td>
                  </tr>
                );
              })}
              {!loading && poMatIds.length === 0 && <tr><td colSpan={poWeeks.length + 2} className="py-10 text-center text-[#64748B]">Açık satın alma siparişi yok.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
